/**
 * Rote Beete — Magier (Beetrix). LMAO-Style (BATTLE-17):
 * "mystische Feuer-Kanalisiererin" — magenta-karminrote Knolle mit spitz
 * zulaufender Wurzel (aufrechte Kegel-Silhouette = Caster, docs/13 §3),
 * oben drei geschwungene Blattstiel-Antennen mit kleinen Blaettern, deren
 * mittlerer wie eine gebogene Hexenhut-Spitze wirkt (Mage-Requisit).
 * Konzentrische Ring-Andeutung im Bauch = Beete-Querschnitt; die Ringe
 * gluehen beim Cast in KEYWORD-Brand-Orange (docs/12 §2 — sie brennt).
 * 2★: gewickeltes Kragen-Band an der Krone + kraeftigere Ringe.
 * 3★: Glut-Runen auf dem aeusseren Ring + permanenter Ember-Schimmer.
 * Referenz-Qualitaet: kohlrabi.ts / okra.ts. Contract: species/types.ts.
 */
import type { PaintParams, SpeciesPainter } from './types'
import { darken, glow, lighten, rgba, rimLight, specular, volumeGradient } from '../draw3d'
import { KEYWORD_COLORS } from '../../ui/theme'

/** verbindliche Brand-Farbe (docs/12 §2) — nur fuer Glut/Glows, nie fuer den Koerper */
const BURN = KEYWORD_COLORS.burn

export const paintRoteBeete: SpeciesPainter = (p: PaintParams) => {
  const { ctx, x, y, r, colors, tier, pose } = p
  const dead = p.emotion === 'dead'
  const cast = dead ? 0 : pose.castPulse
  const outlineW = Math.max(2, r * 0.12)

  ctx.save()
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  ctx.translate(x, y)

  // =====================================================================
  // Blattstiel-Antennen (HINTER dem Korpus — Basis wird von der Knolle
  // verdeckt). Mittlerer Stiel = gebogene "Hexenhut-Spitze", zwei
  // seitliche als Antennen. Jeder Stiel: Outline-Unterlage -> Stiel ->
  // Licht-Kante (Licht oben-links), Blatt am Ende.
  // =====================================================================
  const stemStroke = (path: () => void, w: number): void => {
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = w + outlineW * 0.85
    path()
    ctx.stroke()
    const sg = ctx.createLinearGradient(-r * 0.4, -r * 1.6, r * 0.4, -r * 0.8)
    sg.addColorStop(0, lighten(colors.accent, 16))
    sg.addColorStop(1, darken(colors.accent, 14))
    ctx.strokeStyle = sg
    ctx.lineWidth = w
    path()
    ctx.stroke()
  }
  const leaf = (lx: number, ly: number, ang: number, len: number): void => {
    ctx.save()
    ctx.translate(lx, ly)
    ctx.rotate(ang)
    const lg = ctx.createLinearGradient(-len * 0.3, -len, len * 0.3, 0)
    lg.addColorStop(0, lighten(colors.accent, 22))
    lg.addColorStop(1, darken(colors.accent, 12))
    ctx.fillStyle = lg
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1.2, r * 0.05)
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.quadraticCurveTo(len * 0.42, -len * 0.38, 0, -len)
    ctx.quadraticCurveTo(-len * 0.42, -len * 0.38, 0, 0)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
    // Blattader (Licht-Kante)
    ctx.strokeStyle = rgba(lighten(colors.accent, 38), dead ? 0.3 : 0.8)
    ctx.lineWidth = Math.max(1, r * 0.028)
    ctx.beginPath()
    ctx.moveTo(0, -len * 0.12)
    ctx.lineTo(0, -len * 0.82)
    ctx.stroke()
    ctx.restore()
  }

  const crownY = -r * 0.8
  // mittlerer Stiel: hoch + Spitze kippt nach rechts (Hexenhut-Knick)
  const midStem = (): void => {
    ctx.beginPath()
    ctx.moveTo(0, crownY)
    ctx.bezierCurveTo(-r * 0.1, crownY - r * 0.42, r * 0.2, crownY - r * 0.5, r * 0.34, crownY - r * 0.68)
  }
  // linke Antenne: schwingt weit nach aussen
  const leftStem = (): void => {
    ctx.beginPath()
    ctx.moveTo(-r * 0.18, crownY + r * 0.06)
    ctx.quadraticCurveTo(-r * 0.52, crownY - r * 0.28, -r * 0.58, crownY - r * 0.52)
  }
  // rechte Antenne: kuerzer, gegenlaeufig
  const rightStem = (): void => {
    ctx.beginPath()
    ctx.moveTo(r * 0.2, crownY + r * 0.06)
    ctx.quadraticCurveTo(r * 0.56, crownY - r * 0.12, r * 0.64, crownY - r * 0.38)
  }
  stemStroke(leftStem, Math.max(1.8, r * 0.1))
  leaf(-r * 0.58, crownY - r * 0.52, -0.6, r * 0.52)
  stemStroke(rightStem, Math.max(1.8, r * 0.1))
  leaf(r * 0.64, crownY - r * 0.38, 0.75, r * 0.46)
  stemStroke(midStem, Math.max(2, r * 0.13))
  leaf(r * 0.34, crownY - r * 0.68, 0.9, r * 0.64)

  // =====================================================================
  // Wurzel-Haerchen (unter der Spitze, HINTER dem Korpus)
  // =====================================================================
  ctx.strokeStyle = darken(colors.body, 24)
  ctx.lineWidth = Math.max(1.2, r * 0.05)
  ctx.beginPath()
  ctx.moveTo(r * 0.16, r * 0.78)
  ctx.quadraticCurveTo(r * 0.36, r * 0.92, r * 0.34, r * 1.12)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(-r * 0.18, r * 0.86)
  ctx.quadraticCurveTo(-r * 0.32, r * 1.0, -r * 0.26, r * 1.14)
  ctx.stroke()

  // =====================================================================
  // Korpus: pralle Knolle oben, spitz zulaufende Wurzel unten
  // (aufrechter Kegel = Caster-Silhouette)
  // =====================================================================
  const bodyPath = (): void => {
    ctx.beginPath()
    ctx.moveTo(0, -r * 0.92)
    ctx.bezierCurveTo(r * 0.56, -r * 0.94, r * 0.92, -r * 0.56, r * 0.9, -r * 0.1)
    ctx.bezierCurveTo(r * 0.88, r * 0.42, r * 0.42, r * 0.62, r * 0.08, r * 1.04)
    ctx.quadraticCurveTo(0, r * 1.14, -r * 0.08, r * 1.04)
    ctx.bezierCurveTo(-r * 0.42, r * 0.62, -r * 0.88, r * 0.42, -r * 0.9, -r * 0.1)
    ctx.bezierCurveTo(-r * 0.92, -r * 0.56, -r * 0.56, -r * 0.94, 0, -r * 0.92)
    ctx.closePath()
  }
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  bodyPath()
  ctx.fillStyle = volumeGradient(ctx, 0, -r * 0.08, r, colors.body)
  ctx.fill()
  ctx.stroke()

  // =====================================================================
  // Innenleben, geclippt auf die Knolle
  // =====================================================================
  ctx.save()
  bodyPath()
  ctx.clip()

  // Krone: dunkler Ansatz-Huegel, aus dem die Stiele wachsen
  ctx.fillStyle = rgba(darken(colors.body, 20), 0.9)
  ctx.beginPath()
  ctx.ellipse(0, -r * 0.88, r * 0.3, r * 0.12, 0, 0, Math.PI * 2)
  ctx.fill()

  // Konzentrische Ringe (Beete-Querschnitt) im Bauch. OBEN OFFEN
  // (Bogen rechts -> Bauch -> links), damit sie den Renderer-Mund
  // (y+0.12r..+0.48r) rahmen statt einzukreisen ("Schnuller"-Falle).
  // Jeder Ring: dunkle Furche + Licht-Kante versetzt nach oben-links (3D).
  const ringCx = 0
  const ringCy = r * 0.36
  const ringRadii = tier >= 2 ? [0.56, 0.4, 0.24] : [0.52, 0.32]
  const ringPath = (rr: number, ox: number, oy: number): void => {
    ctx.beginPath()
    ctx.ellipse(ringCx + ox, ringCy + oy, r * rr, r * rr * 0.72, 0, -0.25, Math.PI + 0.25)
  }
  for (const rr of ringRadii) {
    ctx.strokeStyle = rgba(darken(colors.body, 26), dead ? 0.5 : 0.75)
    ctx.lineWidth = Math.max(1.4, r * 0.06)
    ringPath(rr, r * 0.015, r * 0.015)
    ctx.stroke()
    ctx.strokeStyle = rgba(lighten(colors.body, 24), dead ? 0.25 : 0.6)
    ctx.lineWidth = Math.max(1, r * 0.035)
    ringPath(rr, -r * 0.02, -r * 0.02)
    ctx.stroke()
  }

  // Brand-Kern: warmer Ember-Schimmer im Ringzentrum (3★ permanent,
  // sonst nur beim Cast) + Ringe gluehen orange auf (Brand-Keyword)
  if (!dead) {
    const ember = cast * 0.4 + (tier >= 3 ? 0.16 : 0)
    if (ember > 0.01) {
      glow(ctx, ringCx, ringCy, r * 0.62, BURN, ember)
    }
    if (cast > 0.02) {
      ctx.save()
      ctx.shadowColor = BURN
      ctx.shadowBlur = r * (0.14 + cast * 0.22)
      ctx.strokeStyle = rgba(BURN, 0.25 + cast * 0.7)
      ctx.lineWidth = Math.max(1.2, r * 0.05)
      for (const rr of ringRadii) {
        ringPath(rr, 0, 0)
        ctx.stroke()
      }
      // glutheller Kern im innersten Ring
      ctx.fillStyle = rgba(lighten(BURN, 30), cast * 0.55)
      ctx.beginPath()
      ctx.ellipse(ringCx, ringCy, r * 0.09, r * 0.07, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    }
  }
  ctx.restore() // Clip Ende

  // Outline nach dem Innenleben knackig nachziehen (Silhouetten-Readability)
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  bodyPath()
  ctx.stroke()

  // =====================================================================
  // 2★: gewickeltes Kragen-Band an der Krone (Armor-Eskalation)
  // =====================================================================
  if (tier >= 2) {
    const bandY = -r * 0.78
    const bg = ctx.createLinearGradient(0, bandY - r * 0.1, 0, bandY + r * 0.1)
    bg.addColorStop(0, lighten(colors.accent, 14))
    bg.addColorStop(0.5, darken(colors.accent, 4))
    bg.addColorStop(1, darken(colors.accent, 24))
    ctx.fillStyle = bg
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1.5, r * 0.06)
    ctx.beginPath()
    ctx.ellipse(0, bandY, r * 0.4, r * 0.13, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
    // Wickel-Naehte
    ctx.strokeStyle = rgba(lighten(colors.accent, 40), dead ? 0.35 : 0.85)
    ctx.lineWidth = Math.max(1, r * 0.03)
    for (const t of [-0.22, 0, 0.22]) {
      ctx.beginPath()
      ctx.moveTo(t * r - r * 0.05, bandY + r * 0.07)
      ctx.lineTo(t * r + r * 0.05, bandY - r * 0.06)
      ctx.stroke()
    }
  }

  // =====================================================================
  // 3★: Glut-Runen auf dem Bauch — kleine Flammen-Glyphen in Brand-Orange,
  // pulsieren mit castPulse (wie Kohlrabi-Runen, nur Feuer)
  // =====================================================================
  if (tier >= 3 && !dead) {
    const pulse = 0.65 + cast * 0.35
    ctx.save()
    ctx.strokeStyle = rgba(BURN, pulse)
    ctx.lineWidth = Math.max(1.4, r * 0.05)
    ctx.shadowColor = BURN
    ctx.shadowBlur = r * (0.16 + cast * 0.16)
    for (const [gx, gy, s] of [[-0.5, 0.28, 1], [0.52, 0.34, 0.85], [0, 0.78, 0.9]] as const) {
      // kleine Flammen-Glyphe: Zickzack nach oben
      ctx.beginPath()
      ctx.moveTo(gx * r - r * 0.05 * s, gy * r + r * 0.09 * s)
      ctx.lineTo(gx * r + r * 0.04 * s, gy * r + r * 0.02 * s)
      ctx.lineTo(gx * r - r * 0.03 * s, gy * r - r * 0.04 * s)
      ctx.lineTo(gx * r + r * 0.05 * s, gy * r - r * 0.12 * s)
      ctx.stroke()
    }
    ctx.restore()
  }

  // =====================================================================
  // Licht: Rim oben-links + Specular (nie auf Toten)
  // =====================================================================
  if (!dead) {
    rimLight(ctx, 0, -r * 0.1, r * 0.86, r * 0.92, 'rgba(255, 244, 244, 0.5)', Math.max(1.5, r * 0.07))
    specular(ctx, -r * 0.32, -r * 0.5, r * 0.15, 0.5)
  }

  ctx.restore()
}
